'use client';

import { useEffect, useState } from 'react';

/**
 * How often this person plays each game — and so which three are their
 * Favourites.
 *
 * Every account gets its OWN top three (owner's request, 2026-10-09): the
 * shelf used to seed every game as a like for everybody, which made
 * "Favourites" mean nothing. Now it is the three games you actually play most,
 * plus anything you thumbed up yourself.
 *
 * Signed in, the count lives on the account (`/api/games/plays`), so it follows
 * you between devices. Signed out, it lives in this browser.
 *
 * A play is a game left open for PLAY_AFTER_MS. Flicking through the shelf
 * with the arrows opens a game a second and is browsing, not playing.
 */

export const PLAY_AFTER_MS = 10_000;
export const FAVOURITE_COUNT = 3;

/**
 * Everyone's order for the rest of the shelf: most unique players first, then
 * most plays. Production logs 25 Sep – 9 Oct 2026, quick flicks and our own
 * test browsers excluded. A game not listed here (a new one) goes after these.
 */
export const POPULARITY: readonly string[] = [
  'swarm', 'barrage', 'sudoku', 'contra', 'swarmcrypto', 'balloons', 'minesweeper', 'vf2',
  'fruit', 'neonrun', 'nebula', 'emberkeep', 'prism', 'paddle', 'ironline', 'vortex',
  'commando', 'snake', 'pulse', '2048', 'ironlinecrypto', 'turbo', 'depths', 'tanks',
  'fourinarow', 'solitaire', 'drift', 'echo', 'breaker', 'defender', 'coinrun', 'maze',
  'skyclimb', 'moles', 'tictactoe', 'blockfall', 'hop', 'memory', 'stack', 'bubble',
  'crates', 'gems', 'rivercross', 'flow', 'splat',
];

export function popularityRank(id: string): number {
  const i = POPULARITY.indexOf(id);
  return i < 0 ? POPULARITY.length : i;
}

export interface PlayCount { plays: number; lastAt: number }
export type PlayCounts = Record<string, PlayCount>;

const LOCAL_KEY = 'gt_game_plays';
const API = process.env.NEXT_PUBLIC_API_BASE ?? '';

function readLocal(): PlayCounts {
  try {
    const raw = JSON.parse(localStorage.getItem(LOCAL_KEY) || '{}');
    const out: PlayCounts = {};
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      for (const [id, v] of Object.entries(raw as Record<string, PlayCount>)) {
        const plays = Number(v?.plays), lastAt = Number(v?.lastAt);
        if (/^[a-z0-9]{1,24}$/.test(id) && plays > 0) out[id] = { plays, lastAt: lastAt || 0 };
      }
    }
    return out;
  } catch {
    return {};
  }
}

/*
 * One copy shared by every mounted component, like the ratings: a play counted
 * in the game dialog has to reorder the shelf behind it.
 */
let counts: PlayCounts | null = null;
let fetchedFor: string | null = null;
const listeners = new Set<(c: PlayCounts) => void>();

function publish(next: PlayCounts) {
  counts = next;
  listeners.forEach((fn) => fn(next));
}

/** Count one play of `id` — on the account when signed in, on the device always. */
export function recordPlay(id: string, signedIn: boolean) {
  const now = Date.now();
  try {
    const local = readLocal();
    local[id] = { plays: (local[id]?.plays || 0) + 1, lastAt: now };
    localStorage.setItem(LOCAL_KEY, JSON.stringify(local));
  } catch { /* private mode */ }
  const cur = counts ?? {};
  publish({ ...cur, [id]: { plays: (cur[id]?.plays || 0) + 1, lastAt: now } });
  if (signedIn) {
    fetch(`${API}/api/games/play`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game: id }),
    }).catch(() => { /* the local count still moved; the account catches up next play */ });
  }
}

/**
 * This person's play counts. `account` is the signed-in user's id (null when
 * signed out) — a different account in the same browser must not inherit the
 * last one's favourites.
 */
export function useGamePlays(account: string | null): PlayCounts {
  const [state, setState] = useState<PlayCounts>({});
  useEffect(() => {
    listeners.add(setState);
    const key = account ?? '';
    if (fetchedFor !== key) {
      fetchedFor = key;
      // This browser's own counts straight away; signed in, the account's
      // counts replace them once they arrive (and once the account has any).
      publish(readLocal());
      if (account) {
        fetch(`${API}/api/games/plays`, { credentials: 'include', cache: 'no-store' })
          .then((r) => (r.ok ? r.json() : null))
          .then((j) => {
            if (fetchedFor !== key || !j || !Array.isArray(j.plays) || !j.plays.length) return;
            const out: PlayCounts = {};
            for (const p of j.plays) if (p?.game && p.plays > 0) out[p.game] = { plays: p.plays, lastAt: Date.parse(p.lastAt) || 0 };
            publish(out);
          })
          .catch(() => {});
      }
    } else if (counts) setState(counts);
    return () => { listeners.delete(setState); };
  }, [account]);
  return state;
}

/**
 * The three Favourites: most plays, then most recent, then the shelf-wide
 * order. Only games this person can see, and never one they thumbed down.
 */
export function topPlayed(plays: PlayCounts, visible: readonly string[], disliked: (id: string) => boolean): string[] {
  return visible
    .filter((id) => plays[id]?.plays > 0 && !disliked(id))
    .sort((a, b) => plays[b].plays - plays[a].plays || plays[b].lastAt - plays[a].lastAt || popularityRank(a) - popularityRank(b))
    .slice(0, FAVOURITE_COUNT);
}
